import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  Grid,
  GridItem,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Select,
  Button,
  Icon,
  Divider,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Progress
} from '@chakra-ui/react';
import { FaChartBar, FaFax, FaQrcode, FaEye, FaDownload } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';

export default function FaxAnalytics() {
  const [analytics, setAnalytics] = useState({
    fax_stats: {},
    qr_stats: {},
    recent_faxes: [],
    top_qr_codes: [],
    daily_stats: []
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      // Calculate date range
      const now = new Date();
      const daysBack = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const startDate = new Date(now.getTime() - (daysBack * 24 * 60 * 60 * 1000));

      // Fetch fax statistics
      const { data: faxData, error: faxError } = await supabase
        .from('fax_logs')
        .select('*')
        .gte('sent_at', startDate.toISOString());

      if (faxError) throw faxError;

      // Fetch QR code statistics
      const { data: qrData, error: qrError } = await supabase
        .from('qr_codes')
        .select(`
          *,
          qr_visits (
            id,
            visited_at,
            ip_address,
            user_agent
          )
        `)
        .gte('created_at', startDate.toISOString());

      if (qrError) throw qrError;

      // Process fax statistics
      const faxStats = {
        total_sent: faxData?.length || 0,
        successful: faxData?.filter(f => f.status === 'completed').length || 0,
        failed: faxData?.filter(f => f.status === 'failed').length || 0,
        pending: faxData?.filter(f => f.status === 'queued' || f.status === 'sending').length || 0
      };

      // Process QR statistics
      const qrStats = {
        total_codes: qrData?.length || 0,
        active_codes: qrData?.filter(q => q.is_active).length || 0,
        total_visits: qrData?.reduce((sum, q) => sum + (q.qr_visits?.length || 0), 0) || 0,
        unique_visitors: new Set(qrData?.flatMap(q => q.qr_visits?.map(v => v.ip_address) || [])).size
      };

      // Get recent faxes
      const recentFaxes = faxData?.slice(0, 10) || [];

      // Get top QR codes by visits
      const topQRCodes = qrData
        ?.map(q => ({
          ...q,
          visit_count: q.qr_visits?.length || 0
        }))
        .sort((a, b) => b.visit_count - a.visit_count)
        .slice(0, 5) || [];

      // Calculate daily stats
      const dailyStats = calculateDailyStats(faxData, qrData, daysBack);

      setAnalytics({
        fax_stats: faxStats,
        qr_stats: qrStats,
        recent_faxes: recentFaxes,
        top_qr_codes: topQRCodes,
        daily_stats: dailyStats
      });

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateDailyStats = (faxData, qrData, daysBack) => {
    const stats = [];
    for (let i = daysBack - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      const dayFaxes = faxData?.filter(f => 
        f.sent_at?.split('T')[0] === dateStr
      ) || [];

      const dayQRVisits = qrData?.reduce((sum, q) => 
        sum + (q.qr_visits?.filter(v => 
          v.visited_at?.split('T')[0] === dateStr
        ).length || 0), 0) || 0;

      stats.push({
        date: dateStr,
        faxes_sent: dayFaxes.length,
        faxes_successful: dayFaxes.filter(f => f.status === 'completed').length,
        qr_visits: dayQRVisits
      });
    }
    return stats;
  };

  const getSuccessRate = () => {
    const total = analytics.fax_stats.total_sent;
    if (total === 0) return 0;
    return Math.round((analytics.fax_stats.successful / total) * 100);
  };

  if (loading) {
    return (
      <Center h="400px">
        <VStack spacing={4}>
          <Spinner size="lg" color="blue.500" />
          <Text>Loading analytics...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Box>
        <HStack justify="space-between">
          <Box>
            <Text fontSize="xl" fontWeight="bold" mb={2}>
              Fax Analytics
            </Text>
            <Text color="gray.600">
              Track fax performance and QR code engagement
            </Text>
          </Box>
          <HStack>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              size="sm"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </Select>
            <Button
              size="sm"
              leftIcon={<Icon as={FaDownload} />}
              variant="outline"
            >
              Export
            </Button>
          </HStack>
        </HStack>
      </Box>

      {/* Stats Cards */}
      <Grid templateColumns="repeat(4, 1fr)" gap={4}>
        <Card>
          <CardBody>
            <VStack spacing={2}>
              <Icon as={FaFax} boxSize={6} color="blue.500" />
              <Text fontSize="2xl" fontWeight="bold">
                {analytics.fax_stats.total_sent}
              </Text>
              <Text fontSize="sm" color="gray.600">Faxes Sent</Text>
            </VStack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <VStack spacing={2}>
              <Icon as={FaChartBar} boxSize={6} color="green.500" />
              <Text fontSize="2xl" fontWeight="bold">
                {getSuccessRate()}%
              </Text>
              <Text fontSize="sm" color="gray.600">Success Rate</Text>
            </VStack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <VStack spacing={2}>
              <Icon as={FaQrcode} boxSize={6} color="purple.500" />
              <Text fontSize="2xl" fontWeight="bold">
                {analytics.qr_stats.total_codes}
              </Text>
              <Text fontSize="sm" color="gray.600">QR Codes</Text>
            </VStack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <VStack spacing={2}>
              <Icon as={FaEye} boxSize={6} color="orange.500" />
              <Text fontSize="2xl" fontWeight="bold">
                {analytics.qr_stats.total_visits}
              </Text>
              <Text fontSize="sm" color="gray.600">QR Visits</Text>
            </VStack>
          </CardBody>
        </Card>
      </Grid>

      <Grid templateColumns="1fr 1fr" gap={6}>
        {/* Fax Status Breakdown */}
        <Card>
          <CardHeader>
            <Text fontWeight="bold">Fax Status Breakdown</Text>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <HStack>
                  <Box w={3} h={3} bg="green.500" borderRadius="full" />
                  <Text fontSize="sm">Successful</Text>
                </HStack>
                <Badge colorScheme="green">{analytics.fax_stats.successful}</Badge>
              </HStack>
              <Progress
                value={(analytics.fax_stats.successful / Math.max(analytics.fax_stats.total_sent, 1)) * 100}
                colorScheme="green"
                size="sm"
              />

              <HStack justify="space-between">
                <HStack>
                  <Box w={3} h={3} bg="red.500" borderRadius="full" />
                  <Text fontSize="sm">Failed</Text>
                </HStack>
                <Badge colorScheme="red">{analytics.fax_stats.failed}</Badge>
              </HStack>
              <Progress
                value={(analytics.fax_stats.failed / Math.max(analytics.fax_stats.total_sent, 1)) * 100}
                colorScheme="red"
                size="sm"
              />

              <HStack justify="space-between">
                <HStack>
                  <Box w={3} h={3} bg="yellow.500" borderRadius="full" />
                  <Text fontSize="sm">Pending</Text>
                </HStack>
                <Badge colorScheme="yellow">{analytics.fax_stats.pending}</Badge>
              </HStack>
              <Progress
                value={(analytics.fax_stats.pending / Math.max(analytics.fax_stats.total_sent, 1)) * 100}
                colorScheme="yellow"
                size="sm"
              />
            </VStack>
          </CardBody>
        </Card>

        {/* Top QR Codes */}
        <Card>
          <CardHeader>
            <Text fontWeight="bold">Top QR Codes by Visits</Text>
          </CardHeader>
          <CardBody>
            <VStack spacing={3} align="stretch">
              {analytics.top_qr_codes.map((qr, index) => (
                <Box key={qr.id}>
                  <HStack justify="space-between">
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium">
                        #{index + 1} {qr.name}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {new Date(qr.created_at).toLocaleDateString()}
                      </Text>
                    </VStack>
                    <Badge colorScheme="purple">{qr.visit_count} visits</Badge>
                  </HStack>
                  {index < analytics.top_qr_codes.length - 1 && <Divider mt={2} />}
                </Box>
              ))}
              {analytics.top_qr_codes.length === 0 && (
                <Text color="gray.500" textAlign="center" py={4}>
                  No QR codes with visits yet
                </Text>
              )}
            </VStack>
          </CardBody>
        </Card>
      </Grid>

      {/* Recent Faxes */}
      <Card>
        <CardHeader>
          <Text fontWeight="bold">Recent Faxes</Text>
        </CardHeader>
        <CardBody>
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>File</Th>
                  <Th>Recipient</Th>
                  <Th>Status</Th>
                  <Th>Sent At</Th>
                  <Th>Fax ID</Th>
                </Tr>
              </Thead>
              <Tbody>
                {analytics.recent_faxes.map((fax) => (
                  <Tr key={fax.id}>
                    <Td>
                      <Text fontSize="sm" fontWeight="medium">
                        {fax.file_name}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm">{fax.to_fax_number}</Text>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={
                          fax.status === 'completed' ? 'green' :
                          fax.status === 'failed' ? 'red' :
                          fax.status === 'queued' || fax.status === 'sending' ? 'yellow' : 'gray'
                        }
                      >
                        {fax.status}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {fax.sent_at ? new Date(fax.sent_at).toLocaleDateString() : 'N/A'}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="xs" color="gray.500" fontFamily="mono">
                        {fax.fax_id}
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          {analytics.recent_faxes.length === 0 && (
            <Text color="gray.500" textAlign="center" py={8}>
              No recent faxes found
            </Text>
          )}
        </CardBody>
      </Card>

      {/* Daily Activity Chart */}
      <Card>
        <CardHeader>
          <Text fontWeight="bold">Daily Activity</Text>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            {analytics.daily_stats.map((day, index) => (
              <Box key={day.date}>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" fontWeight="medium">
                    {new Date(day.date).toLocaleDateString()}
                  </Text>
                  <HStack spacing={4}>
                    <Badge colorScheme="blue">{day.faxes_sent} faxes</Badge>
                    <Badge colorScheme="purple">{day.qr_visits} QR visits</Badge>
                  </HStack>
                </HStack>
                <Progress
                  value={(day.faxes_sent / Math.max(...analytics.daily_stats.map(d => d.faxes_sent), 1)) * 100}
                  colorScheme="blue"
                  size="sm"
                />
                {index < analytics.daily_stats.length - 1 && <Divider mt={3} />}
              </Box>
            ))}
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
}
